import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveLocale } from '../utils/vukzLocale';
import SEOHead from '../components/seo/SEOHead';
import ProfilesCarousel from '../marketing/ProfilesCarousel';
import AssistProfilesCarousel, { ASSIST_PROFILES_PT } from '../marketing/AssistProfilesCarousel';
import { OrganizationSchema } from '../components/seo/StructuredData';
import {
  ArrowRight,
  BarChart3,
  Activity,
  Users,
  Zap,
  ChevronRight,
  Building2,
  Menu,
  X,
  ClipboardList,
  HeartPulse,
  Gauge,
  LineChart,
  Apple,
  Dumbbell,
  MessageSquare,
  Sparkles,
} from 'lucide-react';

/* ──────────────────────────────────────────────
   VUKZ Brand Tokens — scoped to this page only
   ────────────────────────────────────────────── */
const V = {
  bg: '#ffffff',
  text: '#111111',
  textSoft: '#555555',
  textMute: '#8a8a8a',
  accent: '#111111',
  accentHover: '#333333',
  border: '#e6e6e6',
  cardBg: '#fafafa',
  tagBg: '#f3f3f3',
};

/* ─────── Navegação do header (âncoras) ─────── */
const NAV_LINKS = [
  { href: '#plataforma', label: 'Plataforma' },
  { href: '#assist', label: 'VUKZ Assist' },
  { href: '#perfis', label: 'Perfis' },
  { href: '#funcionalidades', label: 'Funcionalidades' },
  { href: '#planos', label: 'Planos' },
];

/* ─────── Fluxo integrado da plataforma ─────── */
const FLOW = ['Planejamento', 'Treino', 'Execução', 'Carga', 'Wellness', 'Nutrição', 'Performance', 'Gestão'];

/* ─────── Os cinco perfis reais ─────── */
const PROFILES = [
  {
    icon: Activity,
    label: 'Atleta',
    desc: 'A rotina diária em um só lugar.',
    items: ['Treino do dia e execução guiada', 'Check-in de wellness', 'Acompanhamento da própria performance', 'Apoio do VUKZ Assist'],
  },
  {
    icon: Users,
    label: 'Treinador',
    desc: 'Planeje, acompanhe e ajuste com dados.',
    items: ['Gestão de atletas e planejamento de treinos', 'Acompanhamento de execução', 'Wellness do elenco', 'Apoio do Coach Agent'],
  },
  {
    icon: Apple,
    label: 'Nutricionista',
    desc: 'Nutrição esportiva conectada à equipe.',
    items: ['Dashboard nutricional e avaliações', 'Planos alimentares', 'Hidratação e suplementação', 'Alertas nutricionais'],
  },
  {
    icon: Dumbbell,
    label: 'Preparador Físico',
    desc: 'Carga sob controle, dia após dia.',
    items: ['Criação de treinos físicos', 'Monitoramento de carga e ACWR', 'Alertas de risco', 'Apoio do Prep Agent'],
  },
  {
    icon: BarChart3,
    label: 'Gestor',
    desc: 'O clube inteiro em uma visão só.',
    items: ['Indicadores agregados de performance', 'Elenco, staff e categorias', 'Metodologia e planejamento estratégico', 'Gestão da instituição'],
  },
];

/* ─────── Funcionalidades transversais confirmadas ─────── */
const FEATURES = [
  { icon: ClipboardList, label: 'Treino e execução', desc: 'Planeje sessões e acompanhe a execução em fluxos reais.' },
  { icon: HeartPulse, label: 'Wellness e prontidão', desc: 'Check-ins diários para atletas e visão do elenco para o staff.' },
  { icon: Gauge, label: 'Monitoramento de carga', desc: 'Carga de treino e ACWR com alertas para a comissão técnica.' },
  { icon: LineChart, label: 'Performance e evolução', desc: 'Acompanhe resultados e evolução ao longo do tempo.' },
  { icon: Apple, label: 'Nutrição esportiva', desc: 'Avaliações, planos alimentares, hidratação e suplementos.' },
  { icon: Users, label: 'Gestão de elenco e equipe', desc: 'Atletas, staff, categorias e escopos em um só lugar.' },
  { icon: MessageSquare, label: 'Comunicação', desc: 'Mensagens diretas entre as pessoas que operam a equipe.' },
  { icon: Sparkles, label: 'IA por perfil', desc: 'O VUKZ Assist se adapta ao perfil de quem está usando.' },
];

const Container = ({ children, className = '' }) => (
  <div className={`max-w-6xl mx-auto px-6 ${className}`}>{children}</div>
);

/* Slot estático de screenshot — exibe a screenshot real de demonstração quando o
   asset existe em public/screenshots/. Fallback neutro da marca (sem métricas
   simuladas ou dados falsos) enquanto a captura não estiver disponível. */
const ScreenshotSlot = ({ src, alt, label, className = '' }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        role="img"
        aria-label={alt}
        className={`w-full aspect-[16/10] rounded-2xl border flex flex-col items-center justify-center gap-3 ${className}`}
        style={{ borderColor: V.border, backgroundColor: V.cardBg }}
      >
        <img src="/vukz-logo.jpeg" alt="" className="h-10 w-auto" />
        <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: V.textMute }}>
          {label}
        </p>
        <p className="text-[11px]" style={{ color: V.textMute }}>
          Screenshot demo em breve
        </p>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`w-full rounded-2xl border object-cover ${className}`}
      style={{ borderColor: V.border }}
    />
  );
};

/* ═══════════════════════════════════════════════
   VukzHomePage — public marketing shell
   ═══════════════════════════════════════════════ */
const VukzHomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  // Persiste o idioma do site público (vukz_locale) — herdado por /solicitar-acesso
  useEffect(() => { saveLocale('pt'); }, []);

  return (
    <div style={{ backgroundColor: V.bg, color: V.text }} className="min-h-screen font-sans antialiased">
      <SEOHead
        title={null}
        description="VUKZ — Plataforma de inteligência e performance esportiva com IA. Natação, atletismo e futebol."
        path="/"
      />
      <OrganizationSchema />
      {/* Skip link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 px-4 py-2 rounded-lg font-bold"
        style={{ backgroundColor: V.accent, color: '#ffffff' }}
      >
        Pular para conteúdo principal
      </a>

      {/* ── NAVBAR ── */}
      <nav
        className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-md border-b"
        style={{ borderColor: V.border }}
        role="navigation"
        aria-label="Navegação principal"
      >
        <Container className="h-16 flex items-center justify-between gap-4">
          {/* Grupo 1 — Marca */}
          <Link to={isAuthenticated ? '/central' : '/pt'} className="flex items-center gap-3 flex-shrink-0" aria-label="VUKZ - Página inicial">
            <img src="/vukz-logo.jpeg" alt="VUKZ Performance Intelligence Platform" className="h-10 w-auto" />
          </Link>

          {/* Grupo 2 — Navegação (desktop) */}
          <nav className="hidden lg:flex items-center gap-6" aria-label="Seções da página">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm font-medium hover:opacity-70 transition-opacity whitespace-nowrap"
                style={{ color: V.textSoft }}
              >
                {l.label}
              </a>
            ))}
          </nav>

          {/* Grupo 3 — Ações */}
          <div className="flex items-center gap-3">
            <span aria-hidden="true" className="hidden lg:block h-5 w-px" style={{ backgroundColor: V.border }} />

            {/* Seletor de idioma */}
            <div className="flex items-center rounded-lg border overflow-hidden" style={{ borderColor: V.border }} aria-label="Idioma">
              <Link
                to="/"
                className="px-3 py-1.5 text-xs font-semibold hover:bg-gray-100 transition-colors"
                style={{ color: V.textSoft }}
                lang="en"
              >
                EN
              </Link>
              <span
                className="px-3 py-1.5 text-xs font-bold"
                style={{ backgroundColor: V.accent, color: '#fff' }}
                aria-current="true"
                lang="pt-BR"
              >
                PT
              </span>
            </div>

            {isAuthenticated ? (
              <button
                onClick={() => navigate('/central')}
                className="text-sm font-bold px-4 py-2 rounded-lg text-white transition-colors"
                style={{ backgroundColor: V.accent }}
              >
                Entrar no app
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden sm:inline-block text-sm font-semibold px-3 py-2 rounded-lg transition-opacity hover:opacity-70"
                  style={{ color: V.text }}
                >
                  Entrar
                </Link>
                <Link
                  to="/solicitar-acesso"
                  className="hidden sm:inline-block text-sm font-bold px-4 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: V.accent }}
                >
                  Solicitar acesso
                </Link>
              </>
            )}

            {/* Botão do menu mobile */}
            <button
              type="button"
              className="lg:hidden inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold"
              style={{ borderColor: V.border, color: V.text }}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
              aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
              onClick={() => setMobileOpen((o) => !o)}
            >
              {mobileOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
              <span>Menu</span>
            </button>
          </div>
        </Container>

        {/* Painel do menu mobile */}
        {mobileOpen && (
          <div id="mobile-menu" className="lg:hidden border-t bg-white" style={{ borderColor: V.border }}>
            <Container className="py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={closeMobile}
                  className="py-2.5 px-2 rounded-lg text-sm font-medium hover:bg-gray-50"
                  style={{ color: V.textSoft }}
                >
                  {l.label}
                </a>
              ))}
              {!isAuthenticated && (
                <div className="flex items-center gap-3 pt-3 mt-2 border-t" style={{ borderColor: V.border }}>
                  <Link
                    to="/login"
                    onClick={closeMobile}
                    className="flex-1 text-center text-sm font-semibold px-3 py-2.5 rounded-lg border"
                    style={{ borderColor: V.border, color: V.text }}
                  >
                    Entrar
                  </Link>
                  <Link
                    to="/solicitar-acesso"
                    onClick={closeMobile}
                    className="flex-1 text-center text-sm font-bold px-3 py-2.5 rounded-lg text-white"
                    style={{ backgroundColor: V.accent }}
                  >
                    Solicitar acesso
                  </Link>
                </div>
              )}
            </Container>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <main id="main-content" className="pt-28">
        <section className="pb-14">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              {/* Left */}
              <div className="lg:col-span-6">
                <div
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase mb-7"
                  style={{ backgroundColor: V.tagBg, color: V.textSoft }}
                >
                  <Zap size={14} />
                  Gestão esportiva AI-first
                </div>

                <h1 className="text-5xl sm:text-6xl font-black tracking-tight leading-[1.02]">
                  VUKZ
                  <span className="block text-2xl sm:text-3xl font-bold mt-3" style={{ color: V.textSoft }}>
                    Gestão esportiva AI-first, feita para equipes de natação.
                  </span>
                </h1>

                <p className="text-base sm:text-lg mt-6 max-w-xl leading-relaxed" style={{ color: V.textSoft }}>
                  A VUKZ conecta treino, carga, wellness, nutrição, performance e gestão do clube
                  em um único sistema — para atletas, treinadores, nutricionistas, preparadores físicos e gestores.
                </p>

                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 mt-9">
                  {isAuthenticated ? (
                    <button
                      onClick={() => navigate('/central')}
                      className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-white font-bold text-base transition-opacity hover:opacity-90"
                      style={{ backgroundColor: V.accent }}
                    >
                      Entrar no app <ArrowRight size={18} aria-hidden="true" />
                    </button>
                  ) : (
                    <Link
                      to="/solicitar-acesso"
                      className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-white font-bold text-base transition-opacity hover:opacity-90"
                      style={{ backgroundColor: V.accent }}
                    >
                      <Building2 size={18} aria-hidden="true" /> Solicitar acesso <ArrowRight size={18} aria-hidden="true" />
                    </Link>
                  )}
                  <a
                    href="#plataforma"
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 py-3 rounded-xl font-bold text-base border transition-opacity hover:opacity-80"
                    style={{ borderColor: V.border, color: V.textSoft }}
                  >
                    Conhecer a plataforma <ChevronRight size={18} aria-hidden="true" />
                  </a>
                </div>

                <div className="mt-10 flex items-center gap-3 text-xs" style={{ color: V.textMute }}>
                  <span className="inline-flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#22c55e' }} />
                    Infra em Azure
                  </span>
                  <span>•</span>
                  <span>RBAC & módulos enterprise</span>
                </div>
              </div>

              {/* Direita: screenshot atual do produto (showcase sintético) */}
              <div className="lg:col-span-6">
                <ScreenshotSlot
                  src="/marketing/screens/manager-today.png"
                  alt="Tela Hoje do gestor na VUKZ com visão geral do clube (dados sintéticos de demonstração)"
                  label="Visão do gestor — Hoje"
                />
                <p className="text-xs mt-3 text-center" style={{ color: V.textMute }}>
                  Tela real do produto — dados do ambiente de demonstração.
                </p>
              </div>
            </div>
          </Container>
        </section>

        {/* ── PLATAFORMA INTEGRADA ── */}
        <section id="plataforma" className="py-20 border-t" style={{ borderColor: V.border }}>
          <Container>
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">Uma plataforma integrada</h2>
              <p className="text-base mt-3 max-w-xl mx-auto" style={{ color: V.textSoft }}>
                Do planejamento à gestão — cada etapa da rotina da equipe conectada em um único sistema.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2" aria-label="Fluxo da plataforma">
              {FLOW.map((step, i) => (
                <React.Fragment key={step}>
                  <span
                    className="px-4 py-2 rounded-xl border text-sm font-semibold bg-white"
                    style={{ borderColor: V.border, color: V.text }}
                  >
                    {step}
                  </span>
                  {i < FLOW.length - 1 && (
                    <ChevronRight size={16} aria-hidden="true" style={{ color: V.textMute }} />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className="text-xs text-center mt-8" style={{ color: V.textMute }}>
              Foco inicial em natação.
            </p>
          </Container>
        </section>

        {/* ── VUKZ ASSIST — CARROSSEL DE CHATS ── */}
        <AssistProfilesCarousel
          title="Um assistente para cada papel do time"
          subtitle="Cada perfil recebe apoio contextual para organizar a rotina, interpretar dados e tomar decisões com mais clareza."
          ariaLabel="Os cinco chats do VUKZ Assist por perfil"
          inputPlaceholder="Digite sua mensagem"
          prevLabel="Anterior"
          nextLabel="Próximo"
          profiles={ASSIST_PROFILES_PT}
        />

        {/* ── CINCO PERFIS ── */}
        <section id="perfis" className="py-20">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">Cinco perfis, um sistema</h2>
              <p className="text-base mt-3 max-w-xl mx-auto" style={{ color: V.textSoft }}>
                Cada perfil tem seu próprio espaço de trabalho, permissões e fluxos.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {PROFILES.map((p) => (
                <div
                  key={p.label}
                  className="flex flex-col p-6 rounded-2xl border bg-white transition-all hover:shadow-md"
                  style={{ borderColor: V.border }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: V.tagBg }}
                    aria-hidden="true"
                  >
                    <p.icon size={22} style={{ color: V.text }} strokeWidth={2} />
                  </div>
                  <h3 className="text-base font-bold" style={{ color: V.text }}>{p.label}</h3>
                  <p className="text-sm mt-1 mb-4" style={{ color: V.textSoft }}>{p.desc}</p>
                  <ul className="space-y-2 text-sm mt-auto" style={{ color: V.textSoft }}>
                    {p.items.map((it) => (
                      <li key={it} className="flex items-start gap-2.5">
                        <span className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: V.textMute }} aria-hidden="true" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* ── CARROSSEL CINCO VISÕES ── */}
        <ProfilesCarousel
          title="Uma plataforma. Cinco visões. Um mesmo time."
          subtitle="Cada perfil abre o dia com exatamente o que importa: o atleta vê seu plano, o staff vê seus grupos, o gestor vê o clube inteiro."
          ariaLabel="As cinco telas Hoje por perfil"
          slides={[
            { img: 'athlete-today', title: 'Atleta — Hoje', alt: 'Tela Hoje do atleta com prontidão, treino e nutrição', description: 'Prontidão, treino do dia, refeições e hidratação em uma única visão.' },
            { img: 'coach-today', title: 'Treinador — Hoje', alt: 'Tela Hoje do treinador com grupos de treino', description: 'Grupos de treino, volumes e status das sessões de todo o elenco.' },
            { img: 'prep-today', title: 'Preparador físico — Hoje', alt: 'Tela Hoje do preparador com carga e ACWR', description: 'Grupos de força, controle de carga e contexto técnico em leitura.' },
            { img: 'nutritionist-today', title: 'Nutricionista — Hoje', alt: 'Tela Hoje da nutricionista com cobertura de planos por categoria', description: 'Cobertura de planos por categoria e quem precisa de atenção hoje.' },
            { img: 'manager-today', title: 'Gestor — Hoje', alt: 'Tela Hoje do gestor com visão geral do clube', description: 'Visão do clube inteiro: atletas, staff, categorias e atividade do dia.' },
          ]}
        />

        {/* ── FUNCIONALIDADES ── */}
        <section id="funcionalidades" className="py-20" style={{ backgroundColor: V.cardBg }}>
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">O que você já pode fazer hoje</h2>
              <p className="text-base mt-3 max-w-xl mx-auto" style={{ color: V.textSoft }}>
                Capacidades já disponíveis na plataforma.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {FEATURES.map((f) => (
                <div
                  key={f.label}
                  className="p-6 rounded-2xl border bg-white transition-all hover:shadow-md"
                  style={{ borderColor: V.border }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: V.tagBg }}
                    aria-hidden="true"
                  >
                    <f.icon size={22} style={{ color: V.text }} strokeWidth={2} />
                  </div>
                  <h3 className="text-sm font-bold" style={{ color: V.text }}>{f.label}</h3>
                  <p className="text-sm leading-relaxed mt-2" style={{ color: V.textSoft }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* ── PLANOS ── */}
        <section id="planos" className="py-20 border-t" style={{ borderColor: V.border, backgroundColor: V.cardBg }}>
          <Container className="max-w-3xl">
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight">Planos para cada equipe</h2>
              <p className="text-base mt-4 max-w-xl mx-auto" style={{ color: V.textSoft }}>
                Opções para diferentes tamanhos de equipe, acesso progressivo a funcionalidades e
                possibilidade de plano personalizado para organizações maiores.
              </p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-xl text-white font-bold text-base transition-opacity hover:opacity-90"
                style={{ backgroundColor: V.accent }}
              >
                Conhecer os planos <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </div>
          </Container>
        </section>

        {/* ── INFRA ── */}
        <section className="py-14 border-t" style={{ borderColor: V.border }}>
          <Container className="max-w-4xl">
            <div className="flex flex-col items-center text-center gap-6">
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: V.textMute }}>
                Programa e infraestrutura
              </p>
              <div className="flex items-center gap-4">
                <img
                  src="/microsoft-logo.png"
                  alt="Microsoft for Startups"
                  className="h-10 w-auto"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="text-left">
                  <p className="font-bold text-sm" style={{ color: V.text }}>Microsoft for Startups</p>
                  <p className="text-xs" style={{ color: V.textMute }}>Membro do Founders Hub · Construído em Azure</p>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* ── FINAL CTA ── */}
        <section>
          <div className="py-16" style={{ backgroundColor: V.text }}>
            <Container className="max-w-2xl">
              <div className="text-center">
                <Building2 size={32} className="mx-auto mb-6" style={{ color: '#ffffff' }} strokeWidth={1.5} />
                <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                  Ative seu clube na VUKZ
                </h2>
                <p className="text-base mt-4 mb-8" style={{ color: 'rgba(255,255,255,0.65)' }}>
                  Configure seu ambiente, convide seu staff e comece a operar com inteligência esportiva.
                </p>
                <Link
                  to={isAuthenticated ? '/central' : '/solicitar-acesso'}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-base transition-opacity hover:opacity-90"
                  style={{ backgroundColor: '#ffffff', color: V.text }}
                >
                  {isAuthenticated ? 'Ir para o app' : 'Solicitar acesso'} <ArrowRight size={18} />
                </Link>
              </div>
            </Container>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer className="py-8 border-t" style={{ borderColor: V.border, backgroundColor: V.bg }} role="contentinfo">
          <Container>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm" style={{ color: V.textMute }}>
              <div className="text-center sm:text-left">
                <span className="font-bold" style={{ color: V.text }}>VUKZ</span>
                <p className="text-xs mt-1">Gestão esportiva AI-first para equipes de natação.</p>
                <p className="text-xs mt-2">© {new Date().getFullYear()} VUKZ. Todos os direitos reservados.</p>
              </div>
              <nav aria-label="Links do rodapé" className="flex flex-wrap justify-center gap-x-6 gap-y-2">
                <Link to="/pricing" className="hover:underline">Planos</Link>
                <Link to="/login" className="hover:underline">Entrar</Link>
                <Link to="/privacidade" className="hover:underline">Privacidade</Link>
                <Link to="/termos" className="hover:underline">Termos</Link>
                <Link to="/sla" className="hover:underline">SLA</Link>
                <Link to="/contato" className="hover:underline">Contato</Link>
                <Link to="/" className="hover:underline" lang="en">English</Link>
              </nav>
            </div>
          </Container>
        </footer>
      </main>
    </div>
  );
};

export default VukzHomePage;
